import React, { useEffect, createElement } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { Calendar, MessageCircle, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-443">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-444" />
    </svg>;
}
// Language code mapping for <html lang="">
const htmlLangMap: Record<string, string> = {
  en: 'en',
  ua: 'uk',
  ru: 'ru',
  ro: 'ro'
};
// Default (homepage) meta tags per language — used when a page does not
// supply its own SEO title/description via props.
const metaTags: Record<string, {
  title: string;
  description: string;
}> = {
  en: {
    title: 'Private Dentist in Angel, London | Dentico Clinic',
    description: 'Dentico Clinic is a modern private dental clinic in Angel, London, offering general, cosmetic, orthodontic and emergency dental care.'
  },
  ua: {
    title: 'Український стоматолог у Лондоні (Angel) | Dentico Clinic',
    description: 'Dentico Clinic — українська стоматологія в Angel, London EC1V. Консультація українською мовою. Імпланти, вініри, лікування каналів, гігієна та невідкладна допомога. Запис онлайн / WhatsApp / Telegram.'
  },
  ru: {
    title: 'Стоматолог в Лондоне (Angel) | Dentico Clinic',
    description: 'Dentico Clinic — современная частная стоматология в Angel, London EC1V. Консультация на русском языке. Импланты, виниры, лечение каналов, гигиена. Запись онлайн.'
  },
  ro: {
    title: 'Dentist Privat în Angel, Londra | Dentico Clinic',
    description: 'Dentico Clinic — clinică dentară privată modernă în Angel, Londra EC1V. Stomatologie digitală, prețuri transparente, echipă multilingvă. Programare online.'
  }
};
// Canonical and hreflang config
const BASE_URL = 'https://denticoclinic.co.uk';
const hreflangMap: Record<string, string> = {
  en: 'en-GB',
  ua: 'uk-UA',
  ru: 'ru-GB',
  ro: 'ro-RO'
};
interface LayoutProps {
  children: React.ReactNode;
  /** Optional per-page SEO title. Falls back to the language default. */
  title?: string;
  /** Optional per-page SEO meta description. Falls back to the language default. */
  description?: string;
}
export function Layout({
  children,
  title,
  description
}: LayoutProps) {
  const {
    lang
  } = useTranslation();
  // 1) Set <html lang="..."> dynamically
  // 2) Set <title>, <meta>, canonical, hreflang dynamically
  useEffect(() => {
    // Set html lang attribute
    document.documentElement.lang = htmlLangMap[lang] || 'en';
    // Resolve effective meta: per-page props take priority over the
    // language default so each page can have unique SEO metadata.
    const defaults = metaTags[lang] || metaTags.en;
    const meta = {
      title: title || defaults.title,
      description: description || defaults.description
    };
    document.title = meta.title;
    // Set or create meta description
    let descTag = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = meta.description;
    // Set or create og:title
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = meta.title;
    // Set or create og:description
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.content = meta.description;
    // Set or create og:locale
    let ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement | null;
    if (!ogLocale) {
      ogLocale = document.createElement('meta');
      ogLocale.setAttribute('property', 'og:locale');
      document.head.appendChild(ogLocale);
    }
    ogLocale.content = lang === 'ua' ? 'uk_UA' : lang === 'ru' ? 'ru_RU' : 'en_GB';
    // === NEW: Canonical URL ===
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    // Use current path but ensure it starts with the lang prefix
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|ua|ru)/, '');
    canonical.href = `${BASE_URL}/${lang}${pathWithoutLang}`;
    // === NEW: Hreflang tags ===
    // Remove old hreflang tags first
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    (['en', 'ua', 'ru', 'ro'] as const).forEach(l => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflangMap[l];
      link.href = `${BASE_URL}/${l}${pathWithoutLang}`;
      document.head.appendChild(link);
    });
    // Add x-default
    const xDefault = document.createElement('link');
    xDefault.rel = 'alternate';
    xDefault.hreflang = 'x-default';
    xDefault.href = `${BASE_URL}/en${pathWithoutLang}`;
    document.head.appendChild(xDefault);
    // === NEW: Robots meta ===
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = 'index,follow,max-image-preview:large';
    // === NEW: Content-Language meta ===
    let contentLang = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement | null;
    if (!contentLang) {
      contentLang = document.createElement('meta');
      contentLang.httpEquiv = 'content-language';
      document.head.appendChild(contentLang);
    }
    contentLang.content = htmlLangMap[lang] || 'en';
  }, [lang, title, description]);
  // 3) Enhanced structured data with full Schema.org markup
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': ['Dentist', 'MedicalBusiness', 'LocalBusiness'],
    '@id': 'https://denticoclinic.co.uk/#denticoclinic',
    name: 'Dentico Clinic',
    url: lang === 'ua' ? 'https://denticoclinic.co.uk/ua' : lang === 'ru' ? 'https://denticoclinic.co.uk/ru' : lang === 'ro' ? 'https://denticoclinic.co.uk/ro' : 'https://denticoclinic.co.uk',
    image: 'https://cdn.magicpatterns.com/uploads/kCd6PjT8cHCFux7c2JDXSh/7800B156-90A9-4047-97F1-B20E4E965E7F.png',
    logo: 'https://cdn.magicpatterns.com/uploads/oHJPxkkc7f6GgU5uyo3LQX/ChatGPT_Image_Feb_5,_2026,_03_12_40_PM.png',
    description: lang === 'ua' ? 'Dentico Clinic — сучасна приватна стоматологія в Angel, Central London (EC1V). Консультація українською мовою. Огляди, гігієна, пломби, коронки, вініри, імпланти та невідкладна допомога.' : lang === 'ru' ? 'Dentico Clinic — современная частная стоматология в Angel, Central London (EC1V). Консультация на русском языке. Осмотры, гигиена, пломбы, коронки, виниры, импланты.' : 'Dentico Clinic — modern private dental clinic in Angel, Central London (EC1V). Consultations available in Ukrainian. Check-ups, hygiene, fillings, crowns, veneers, implants & emergency dental care.',
    telephone: '+44 7345 197820',
    email: 'info@denticoclinic.co.uk',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '360–364 City Road',
      addressLocality: 'London',
      postalCode: 'EC1V 2PY',
      addressCountry: 'GB'
    },
    areaServed: [{
      '@type': 'City',
      name: 'London'
    }, {
      '@type': 'AdministrativeArea',
      name: 'Islington'
    }],
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.5308911,
      longitude: -0.1029003
    },
    hasMap: 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6',
    priceRange: '££',
    knowsLanguage: ['en', 'uk', 'ru', 'ro'],
    medicalSpecialty: ['Dentistry', 'Cosmetic Dentistry', 'Endodontics', 'Dental Implants', 'Restorative Dentistry'],
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    }, {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '10:00',
      closes: '16:00'
    }],
    contactPoint: [{
      '@type': 'ContactPoint',
      contactType: 'appointments',
      telephone: '+44 7345 197820',
      email: 'info@denticoclinic.co.uk',
      availableLanguage: ['English', 'Ukrainian', 'Russian', 'Romanian']
    }],
    sameAs: ['https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6', 'https://instagram.com/dentico_clinic'],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '30',
      bestRating: '5'
    },
    potentialAction: [{
      '@type': 'ReserveAction',
      name: lang === 'ua' ? 'Записатись на прийом' : lang === 'ru' ? 'Записаться на приём' : 'Book an appointment',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: BOOKING_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }, {
      '@type': 'CommunicateAction',
      name: 'WhatsApp',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: WHATSAPP_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }, {
      '@type': 'CommunicateAction',
      name: 'Telegram',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: TELEGRAM_URL,
        inLanguage: htmlLangMap[lang] || 'en',
        actionPlatform: ['http://schema.org/DesktopWebPlatform', 'http://schema.org/MobileWebPlatform']
      }
    }]
  };
  return <div className="min-h-screen flex flex-col bg-ivory" data-id="element-445">
      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(structuredData)
    }} data-id="element-446" />

      <Header data-id="element-447" />
      <main className="flex-grow" data-id="element-448">{children}</main>
      <Footer data-id="element-449" />

      {/* Sticky Mobile Booking Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-ivory/95 backdrop-blur-sm border-t border-beige/60 shadow-[0_-4px_16px_rgba(47,74,63,0.08)] px-3 py-2.5 safe-area-bottom" data-id="element-450">
        <div className="flex gap-2 items-stretch" data-id="element-451">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="flex-1" data-id="element-452">
            <button className="w-full h-full flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm py-2.5 px-3 rounded-xl transition-colors" data-id="element-453">
              <Calendar className="w-4 h-4" data-id="element-454" />
              {lang === 'en' ? 'Book Online' : lang === 'ua' ? 'Записатись' : lang === 'ro' ? 'Programare' : 'Записаться'}
            </button>
          </a>
          <a href="tel:+447345197820" className="flex-shrink-0" data-id="element-455">
            <button aria-label="Call clinic" className="h-full flex items-center justify-center bg-ivory-200 border border-beige/60 text-forest-700 hover:bg-stone-100 py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-456">
              <Phone className="w-5 h-5" strokeWidth={1.75} data-id="element-457" />
            </button>
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-id="element-458">
            <button aria-label="WhatsApp" className="h-full flex items-center justify-center bg-ivory-200 border border-beige/60 text-forest-700 hover:bg-stone-100 py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-459">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} data-id="element-460" />
            </button>
          </a>
          <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-id="element-461">
            <button aria-label="Telegram" className="h-full flex items-center justify-center bg-ivory-200 border border-beige/60 text-forest-700 hover:bg-stone-100 py-2.5 px-3.5 rounded-xl transition-colors" data-id="element-462">
              <TelegramIcon className="w-5 h-5" data-id="element-463" />
            </button>
          </a>
        </div>
      </div>

      <style data-id="element-464">{`
        .safe-area-bottom {
          padding-bottom: max(0.625rem, env(safe-area-inset-bottom));
        }
        @media (max-width: 767px) {
          main { padding-bottom: 72px; }
        }
      `}</style>
    </div>;
}